import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { PersonalActivity } from '@tt/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Textarea } from '@/components/ui/input';
import { Alert } from '@/components/ui/misc';
import { isApiError } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { useCreatePersonalActivity } from '@/lib/queries';

interface NewActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The project the activity will belong to. An activity belongs to exactly one project
   * (design §7.3), so there is nothing to create without one — the caller only opens this
   * dialog once a project is chosen, and the submit button says so if it is not.
   */
  projectId: string | null;
  projectName?: string;
  initialName?: string;
  /** Called with the created activity, or with the suggested existing one when the user picks it. */
  onCreated: (activity: { id: string; name: string }) => void;
}

/** FR4: create a personal activity from the entry screen, with duplicate suggestions. */
export function NewActivityDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  initialName = '',
  onCreated,
}: NewActivityDialogProps) {
  const { t } = useTranslation();
  const create = useCreatePersonalActivity();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription('');
      setError(null);
      setSuggestion(null);
    }
  }, [open, initialName]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!projectId) return;
    setError(null);
    setSuggestion(null);
    create.mutate(
      { projectId, name, description: description || null },
      {
        onSuccess: (activity: PersonalActivity) => {
          toast.success(t('time.activityCreated'));
          onCreated({ id: activity.id, name: activity.name });
          onOpenChange(false);
        },
        onError: (err) => {
          if (isApiError(err) && err.code === 'DUPLICATE_NAME') {
            const s = err.problem.suggestion as { id: string; name: string } | undefined;
            if (s) setSuggestion(s);
            setError(t('time.duplicateActivity', { name: s?.name ?? name }));
          } else {
            setError(describeError(err, t));
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('time.newActivityTitle')}</DialogTitle>
            <DialogDescription>
              {projectName
                ? t('time.newActivityForProject', { project: projectName })
                : t('time.newActivityHelp')}
            </DialogDescription>
          </DialogHeader>
          {error ? (
            <Alert variant="warning">
              <p>{error}</p>
              {suggestion ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => {
                    onCreated(suggestion);
                    onOpenChange(false);
                  }}
                >
                  {t('time.useExisting')}
                </Button>
              ) : null}
            </Alert>
          ) : null}
          <Field label={t('time.activityName')} required>
            {(p) => (
              <Input
                {...p}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                required
                autoFocus
              />
            )}
          </Field>
          <Field label={`${t('time.description')} (${t('app.optional')})`}>
            {(p) => (
              <Textarea
                {...p}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('app.cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim() || !projectId}>
              {t('app.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
